import { produce } from 'immer';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { actionNodeDetachOffset } from 'flow-builder/constants';
import { ConnectionMap } from 'flow-builder/types/connection';
import { NodeAttributes } from 'flow-builder/types/node';
import type { Relation, RelationMap } from 'flow-builder/types/relation';
import { NodeState } from 'flow-builder/types/state';
import {
  getChildEndX,
  getChildEndY,
} from 'flow-builder/utils/canvas/calculateChildPosition/calculateChildPosition';
import { getNodeComponent } from 'flow-builder/utils/node/getNodeComponent/getNodeComponent';
import { getRootTree } from 'flow-builder/utils/node/getNodeTree/getNodeTree';
import { getScopeVars } from 'flow-builder/utils/scopeVars/getScopeVars/getScopeVars';

type NodeDisconnectProps = {
  setNodeState: Dispatch<SetStateAction<NodeState>>;
  nodeAttributes: NodeAttributes;
};

type NodeDisconnect = (
  props: NodeDisconnectProps,
) => (id: Relation['id']) => () => void;

export const createAttachNode = (partialNode: Partial<Relation>): Relation => ({
  id: uuid(),
  parentId: null,
  isAction: true,
  isEditable: false,
  isRemovable: true,
  connectionLabel: 'arrow',
  nodeType: FlowNodeTypes.ACTION,
  pageX: 0,
  pageY: 0,
  /*
   * Height on action/attach node is unimportant
   * as it's never going to be connected to anything.
   */
  height: 0,
  badges: [],
  connections: [],
  children: [],
  childIds: [],
  payload: null,
  ...partialNode,
});

export const replaceNodeWithAction = (
  connections: ConnectionMap,
  relations: RelationMap,
  nodeId: Relation['id'],
  disconnectNodeId: Relation['id'],
  nodeAttributes: NodeAttributes,
): void => {
  const { children, connections: nodeConnections } = relations[nodeId];
  const disconnectNode = relations[disconnectNodeId];
  const { parentId, connectionLabel, pageX, pageY } = disconnectNode;

  const connectionIndex = nodeConnections.findIndex(
    ({ id, connection }) => (connection || id) === disconnectNodeId,
  );
  const childIndex = children.indexOf(disconnectNodeId);

  if (!parentId) {
    throw new Error(
      'Cannot replace node with action because node to replace has no parent.',
    );
  }

  /*
   * Create a temporary connection node.
   */
  const connectionNode: Relation = createAttachNode({
    parentId,
    connectionLabel,
    pageX: pageX,
    pageY: pageY + actionNodeDetachOffset,
  });
  const disconnectNodeParent = relations[parentId];

  nodeConnections.splice(connectionIndex, 1, {
    id: connectionNode.id,
    connection: connectionNode.id,
    label: connectionNode.connectionLabel,
  });
  children.splice(childIndex, 1, connectionNode.id);

  relations[parentId] = getNodeComponent(
    nodeAttributes,
    disconnectNodeParent,
  ).updateConnection(disconnectNodeParent, disconnectNode, connectionNode.id);

  relations[connectionNode.id] = connectionNode;
  connections[connectionNode.id] = {
    ...connections[disconnectNodeId],
    id: connectionNode.id,
    target: {
      // Unimportant for action node.
      height: 0,
    },
    endX: getChildEndX(connectionNode.pageX),
    endY: getChildEndY(connectionNode.pageY),
  };
};

export const useNodeDisconnect: NodeDisconnect = ({
  setNodeState,
  nodeAttributes,
}) => {
  return useCallback(
    (id) => () =>
      setNodeState(
        produce((state) => {
          const { relations, connections } = state;
          const { parentId } = relations[id];

          if (!parentId) {
            throw new Error('Missing parent id');
          }

          if (parentId) {
            replaceNodeWithAction(
              connections,
              relations,
              parentId,
              id,
              nodeAttributes,
            );
          }

          relations[id].parentId = null;
          state.scopeVars = getScopeVars(nodeAttributes, relations);
          state.rootTree = getRootTree(relations);

          delete connections[id];
        }),
      ),
    [setNodeState, nodeAttributes],
  );
};
